import React, { useState } from 'react';
import { Button } from './ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from './ui/dialog';
import { Calendar } from './ui/calendar';
import { Checkbox } from './ui/checkbox';
import { Label } from './ui/label';
import { toast } from 'sonner';

const toYMD = (d) => {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
};

export const DuplicateShiftDialog = ({ shift, token, apiCall, onDone }) => {
  const [open, setOpen] = useState(false);
  const [dates, setDates] = useState([]);
  const [keepAssignment, setKeepAssignment] = useState(false);
  const [busy, setBusy] = useState(false);

  const submit = async () => {
    if (!dates.length) {
      toast.error('Select at least one date');
      return;
    }
    setBusy(true);
    try {
      const res = await apiCall(`/shifts/${shift.id}/duplicate`, 'POST', {
        dates: dates.map(toYMD),
        keep_assignment: keepAssignment,
      }, token);
      toast.success(res.message);
      setOpen(false);
      setDates([]);
      setKeepAssignment(false);
      onDone();
    } catch (e) {
      toast.error(e.detail || 'Failed to duplicate shift');
    }
    setBusy(false);
  };

  return (
    <>
      <Button size="sm" variant="outline" className="border-blue-300 text-blue-700 hover:bg-blue-50"
        onClick={() => setOpen(true)} data-testid={`duplicate-shift-btn-${shift.id}`}>
        ⧉ Duplicate
      </Button>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Duplicate shift to other days</DialogTitle>
            <DialogDescription>
              {shift.title} — {shift.start_time} to {shift.end_time} at {shift.location}. Pick one or more dates;
              an identical shift is created for each day.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div className="flex justify-center border rounded-md" data-testid="duplicate-shift-calendar">
              <Calendar mode="multiple" selected={dates} onSelect={(d) => setDates(d || [])} />
            </div>
            {dates.length > 0 && (
              <p className="text-sm text-slate-600">
                {dates.length} day(s) selected: {dates.map(toYMD).sort().map(d => d.split('-').reverse().join('/')).join(', ')}
              </p>
            )}
            {shift.assigned_to && (
              <div className="flex items-center space-x-2">
                <Checkbox id={`keep-assign-${shift.id}`} checked={keepAssignment}
                  onCheckedChange={(v) => setKeepAssignment(!!v)} data-testid="duplicate-keep-assignment" />
                <Label htmlFor={`keep-assign-${shift.id}`} className="text-sm">
                  Assign the new shifts to the same staff member
                </Label>
              </div>
            )}
            <div className="flex justify-end gap-2">
              <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
              <Button className="bg-blue-600 hover:bg-blue-700" disabled={busy || !dates.length}
                onClick={submit} data-testid="duplicate-shift-submit">
                Create {dates.length || ''} shift{dates.length === 1 ? '' : 's'}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
};
